(() => {
  'use strict';
  if (window.__tglSafeLimitsLoaded) return;
  window.__tglSafeLimitsLoaded = true;

  const MARY_15 = '66870680453424';
  const MARY_30 = '66870665150768';
  const DEFAULT_LIMIT = 10;
  const bypassForms = new WeakSet();
  const bypassCheckout = new WeakSet();
  let cartSyncTimer = null;

  const digits = (value) => String(value == null ? '' : value).replace(/\D/g, '');
  const limitForVariant = (variantId) => {
    const id = digits(variantId);
    if (id === MARY_30) return 2;
    if (id === MARY_15) return 5;
    return DEFAULT_LIMIT;
  };
  const isMaryVariant = (variantId) => [MARY_15, MARY_30].includes(digits(variantId));
  const messageFor = (limit, variantId) =>
    isMaryVariant(variantId)
      ? `Limit ${limit} per order for this size.`
      : `Limit ${limit} per order for this product.`;

  function notify(message) {
    let toast = document.querySelector('.tgl-safe-limit-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'tgl-safe-limit-toast';
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'assertive');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(notify.timer);
    notify.timer = setTimeout(() => toast.classList.remove('is-visible'), 4000);
  }

  async function cartData() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await window.fetch(root + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  }

  function formField(form, name) {
    const associated = form?.elements?.namedItem?.(name);
    if (associated && associated.value != null) return associated;
    return form?.querySelector?.(`[name="${name}"]`) || null;
  }

  function selectedVariant(form) {
    return digits(formField(form, 'id')?.value);
  }

  function currentProductId(form) {
    const metaId = window.meta?.product?.id;
    const field = form?.querySelector('[name="product-id"], [name="product_id"], [data-product-id]');
    return digits(metaId || field?.value || field?.dataset?.productId);
  }

  function populateStickyQuantity(select, limit) {
    if (!select || select.tagName !== 'SELECT') return;
    const previous = Math.min(limit, Math.max(1, Number(select.value || 1)));
    const expected = Array.from({ length: limit }, (_, index) => String(index + 1));
    const current = Array.from(select.options).map((option) => option.value);
    if (current.length !== expected.length || current.some((value, index) => value !== expected[index])) {
      select.replaceChildren(...expected.map((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        return option;
      }));
    }
    select.value = String(previous);
  }

  function productQuantityInCart(cart, variantId, productId) {
    const target = cart.items.find((item) => digits(item.variant_id) === digits(variantId));
    if (isMaryVariant(variantId)) {
      return cart.items
        .filter((item) => digits(item.variant_id) === digits(variantId))
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    }
    const resolvedProductId = digits(productId || target?.product_id);
    if (resolvedProductId) {
      return cart.items
        .filter((item) => digits(item.product_id) === resolvedProductId)
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    }
    return cart.items
      .filter((item) => digits(item.variant_id) === digits(variantId))
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  }

  function applyProductUI() {
    if (!location.pathname.includes('/products/')) return;
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      const variantId = selectedVariant(form);
      if (!variantId) return;
      const isSticky = Boolean(form.closest('sticky-add-to-cart'));
      const limit = limitForVariant(variantId);
      const inputs = Array.from(form.elements || []).filter((element) => element.name === 'quantity');

      inputs.forEach((input) => {
        const quantityBox = input.closest('quantity-input, .quantity') || input.parentElement;
        if (isSticky) {
          populateStickyQuantity(input, limit);
          input.removeAttribute('max');
          delete input.dataset.tglLimit;
          quantityBox?.classList.remove('tgl-main-quantity');
          quantityBox?.querySelector('.tgl-safe-limit-note')?.remove();
          if (quantityBox?.nextElementSibling?.classList.contains('tgl-safe-limit-note')) {
            quantityBox.nextElementSibling.remove();
          }
          return;
        }

        input.max = String(limit);
        input.dataset.tglLimit = String(limit);
        input.dataset.tglOrderLimit = String(limit);
        input.dataset.tglVariant = variantId;
        if (Number(input.value || 1) > limit) input.value = String(limit);
        if (!quantityBox) return;

        quantityBox.classList.add('tgl-main-quantity');
        quantityBox.querySelector('.tgl-safe-limit-note')?.remove();
        let note = quantityBox.nextElementSibling;
        if (!isMaryVariant(variantId)) {
          if (note?.classList.contains('tgl-safe-limit-note')) note.remove();
          return;
        }
        if (!note?.classList.contains('tgl-safe-limit-note')) {
          note = document.createElement('p');
          note.className = 'tgl-safe-limit-note';
          quantityBox.insertAdjacentElement('afterend', note);
        }
        note.textContent = messageFor(limit, variantId);
      });
    });
  }

  function scheduleCartSync(delay = 120) {
    clearTimeout(cartSyncTimer);
    cartSyncTimer = setTimeout(syncCartUI, delay);
  }

  async function syncCartUI() {
    const cartInputs = Array.from(document.querySelectorAll(
      'cart-drawer input[name="updates[]"], .cart-drawer input[name="updates[]"], #CartDrawer input[name="updates[]"], form[action*="/cart"] input[name="updates[]"]'
    ));
    if (!cartInputs.length) return;
    try {
      const cart = await cartData();
      cartInputs.forEach((input, index) => {
        const item = cart.items[index];
        if (!item) return;
        const variantId = digits(item.variant_id);
        const limit = limitForVariant(variantId);
        let maximum = limit;
        if (!isMaryVariant(variantId)) {
          const otherQuantity = cart.items
            .filter((other, otherIndex) =>
              otherIndex !== index && String(other.product_id) === String(item.product_id)
            )
            .reduce((sum, other) => sum + Number(other.quantity || 0), 0);
          maximum = Math.max(0, limit - otherQuantity);
        }
        input.max = String(maximum);
        input.dataset.tglLimit = String(maximum);
        input.dataset.tglOrderLimit = String(limit);
        input.dataset.tglVariant = variantId;
        input.closest('quantity-input')?.validateQtyRules?.();
      });
    } catch (_) {}
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest?.('form[action*="/cart/add"]');
    if (!form) return;
    if (bypassForms.has(form)) {
      bypassForms.delete(form);
      return;
    }

    const variantId = selectedVariant(form);
    if (!variantId) return;
    const quantity = Math.max(1, Number(formField(form, 'quantity')?.value || 1));
    const limit = limitForVariant(variantId);
    const productId = currentProductId(form);
    const submitter = event.submitter;
    event.preventDefault();
    event.stopImmediatePropagation();

    try {
      const cart = await cartData();
      const current = productQuantityInCart(cart, variantId, productId);
      if (current + quantity > limit) {
        notify(messageFor(limit, variantId));
        return;
      }
    } catch (_) {
      // If the precheck is unavailable, preserve the store's normal add-to-cart flow.
    }

    bypassForms.add(form);
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter || undefined);
    } else {
      form.submit();
    }
    scheduleCartSync(400);
    setTimeout(() => scheduleCartSync(0), 900);
  }, true);

  document.addEventListener('input', (event) => {
    const input = event.target.closest?.('input[data-tgl-limit]');
    if (!input) return;
    const maximum = Number(input.dataset.tglLimit);
    if (Number(input.value) > maximum) {
      input.value = String(maximum);
      const orderLimit = Number(input.dataset.tglOrderLimit || maximum);
      notify(messageFor(orderLimit, input.dataset.tglVariant || selectedVariant(input.closest('form'))));
    }
  }, true);

  document.addEventListener('change', (event) => {
    if (event.target.matches?.('[name="id"], select, input[type="radio"]')) {
      setTimeout(applyProductUI, 0);
    }
    if (event.target.matches?.('input[name="updates[]"]')) {
      setTimeout(syncCartUI, 350);
    }
  }, true);

  function cartViolation(cart) {
    const totals = new Map();
    for (const item of cart.items || []) {
      const variantId = digits(item.variant_id);
      const productId = digits(item.product_id);
      const key = isMaryVariant(variantId) ? `variant:${variantId}` : `product:${productId}`;
      const current = totals.get(key) || { quantity: 0, variantId };
      current.quantity += Number(item.quantity || 0);
      totals.set(key, current);
    }
    for (const total of totals.values()) {
      const limit = limitForVariant(total.variantId);
      if (total.quantity > limit) return { limit, variantId: total.variantId };
    }
    return null;
  }

  document.addEventListener('click', async (event) => {
    const target = event.target;
    const plus = target.closest?.(
      'cart-drawer .quantity-button[name="plus"], .cart-drawer .quantity-button[name="plus"], #CartDrawer .quantity-button[name="plus"]'
    );
    if (plus) {
      const input = plus.closest('quantity-input')?.querySelector('input[name="updates[]"]');
      const maximum = Number(input?.dataset.tglLimit || input?.max || 0);
      if (input && maximum && Number(input.value) >= maximum) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const orderLimit = Number(input.dataset.tglOrderLimit || maximum);
        notify(messageFor(orderLimit, input.dataset.tglVariant));
        return;
      }
    }

    const checkout = target.closest?.(
      'button[name="checkout"], input[name="checkout"], a[href*="/checkout"]'
    );
    if (checkout && !bypassCheckout.has(checkout)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      try {
        const violation = cartViolation(await cartData());
        if (violation) {
          notify(messageFor(violation.limit, violation.variantId));
          scheduleCartSync(0);
          return;
        }
      } catch (_) {}
      bypassCheckout.add(checkout);
      checkout.click();
      bypassCheckout.delete(checkout);
      return;
    }

    if (target.closest?.('form[action*="/cart/add"], quantity-input, .quantity, cart-drawer, .cart-drawer, #CartDrawer, a[href*="/cart"]')) {
      setTimeout(applyProductUI, 0);
      scheduleCartSync(350);
    }
  }, true);

  function observeCartDrawer() {
    const drawer = document.querySelector('cart-drawer, .cart-drawer, #CartDrawer');
    if (!drawer || drawer.dataset.tglLimitObserver === 'true') return;
    drawer.dataset.tglLimitObserver = 'true';
    new MutationObserver(() => scheduleCartSync(100)).observe(drawer, {
      childList: true,
      subtree: true
    });
    scheduleCartSync(0);
  }

  const style = document.createElement('style');
  style.textContent = `
    .tgl-main-quantity {
      display: grid !important;
      grid-template-columns: 3.8rem minmax(3.6rem, 1fr) 3.8rem;
      align-items: center;
      width: min(100%, 16.5rem) !important;
      min-height: 4.6rem;
      overflow: hidden;
      border: 1px solid rgba(var(--color-foreground, 31, 31, 31), .18) !important;
      border-radius: 999px !important;
      background: linear-gradient(135deg, #fff 0%, #faf7f4 100%);
      box-shadow: 0 .45rem 1.4rem rgba(35, 30, 26, .08);
    }
    .tgl-main-quantity::before,
    .tgl-main-quantity::after {
      display: none !important;
    }
    .tgl-main-quantity .quantity-button,
    .tgl-main-quantity .quantity__button {
      width: 3.8rem !important;
      min-width: 3.8rem;
      height: 100%;
      min-height: 4.4rem;
      padding: 0;
      border: 0;
      background: transparent;
      color: rgb(var(--color-foreground, 31, 31, 31));
      transition: background .2s ease, color .2s ease;
    }
    .tgl-main-quantity .quantity-button:hover,
    .tgl-main-quantity .quantity__button:hover {
      background: #251f1b;
      color: #fff;
    }
    .tgl-main-quantity .quantity-input,
    .tgl-main-quantity .quantity__input,
    .tgl-main-quantity input[name="quantity"] {
      width: 100% !important;
      min-width: 0;
      padding: 0 !important;
      border: 0 !important;
      background: transparent !important;
      color: #251f1b;
      font-size: 1.55rem !important;
      font-weight: 700;
      text-align: center;
      box-shadow: none !important;
    }
    .tgl-safe-limit-note {
      display: inline-flex;
      align-items: center;
      width: auto;
      max-width: 100%;
      margin: .75rem 0 0;
      padding: .65rem 1rem;
      border: 1px solid rgba(128, 28, 69, .14);
      border-radius: 999px;
      background: rgba(128, 28, 69, .06);
      color: rgba(var(--color-foreground, 31, 31, 31), .78);
      font-size: 1.2rem;
      line-height: 1.3;
      white-space: normal;
    }
    .tgl-safe-limit-toast {
      position: fixed;
      right: 1.2rem;
      bottom: calc(1.2rem + env(safe-area-inset-bottom));
      left: 1.2rem;
      z-index: 2147483647;
      max-width: 42rem;
      margin: auto;
      padding: 1.15rem 1.4rem;
      border-radius: .8rem;
      background: #1f1f1f;
      color: #fff;
      box-shadow: 0 .6rem 2rem rgba(0,0,0,.2);
      font-size: 1.4rem;
      line-height: 1.35;
      text-align: center;
      opacity: 0;
      pointer-events: none;
      transform: translateY(1rem);
      transition: opacity .2s ease, transform .2s ease;
    }
    .tgl-safe-limit-toast.is-visible {
      opacity: 1;
      transform: translateY(0);
    }
  `;
  document.head.appendChild(style);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      applyProductUI();
      observeCartDrawer();
      syncCartUI();
    }, { once: true });
  } else {
    applyProductUI();
    observeCartDrawer();
    syncCartUI();
  }
  document.addEventListener('shopify:section:load', applyProductUI);
  window.addEventListener('pageshow', applyProductUI);
})();